import { cn } from '../../lib/utils';

interface AvatarProps {
  src?: string | null;
  name?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizes = {
  sm: 'w-7 h-7 text-xs',
  md: 'w-9 h-9 text-sm',
  lg: 'w-12 h-12 text-base',
};

function getInitials(name?: string | null): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

/** Deterministic pastel background from name */
function nameColor(name?: string | null): string {
  const palette = [
    'bg-indigo-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-emerald-500',
    'bg-sky-500',
    'bg-orange-500',
    'bg-rose-500',
    'bg-teal-500',
  ];
  if (!name) return palette[0];
  const idx = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0) % palette.length;
  return palette[idx];
}

export default function Avatar({ src, name, size = 'md', className }: AvatarProps) {
  return (
    <span
      className={cn(
        'relative inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 select-none',
        sizes[size],
        !src && nameColor(name),
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={name ?? 'avatar'}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <span className="font-medium text-white leading-none">{getInitials(name)}</span>
      )}
    </span>
  );
}
